/**
 * Camera utility functions for global camera management
 */

/**
 * Safely parse user session data from sessionStorage
 * @returns {Object|null} Parsed user data or null if invalid
 */
export const getSafeUserSession = () => {
  try {
    const userSession = sessionStorage.getItem('user');
    if (!userSession) {
      console.warn('No user session found in sessionStorage');
      return null;
    }

    const userData = JSON.parse(userSession);

    if (
      !userData ||
      !userData.signInUserSession ||
      !userData.signInUserSession.idToken
    ) {
      console.warn('Invalid user session data structure');
      return null;
    }

    return userData;
  } catch (error) {
    console.error('Error parsing user session data:', error);
    return null;
  }
};

/**
 * Get the current camera preference from localStorage
 * @returns {string} The selected camera value
 */
export const getSelectedCamera = () => {
  return localStorage.getItem('selectedCamera') || 'user';
};

/**
 * Get video constraints based on the selected camera
 * @param {string} selectedCamera - The selected camera value
 * @param {Object} options - Additional options for video constraints
 * @returns {Object} Video constraints object
 */
export const getVideoConstraints = (selectedCamera = null, options = {}) => {
  const camera = selectedCamera || getSelectedCamera();
  const defaultOptions = {
    width: 1280,
    height: 720,
    ...options
  };

  if (camera === 'user') {
    return {
      ...defaultOptions,
      facingMode: 'user'
    };
  } else if (camera === 'environment') {
    return {
      ...defaultOptions,
      facingMode: 'environment'
    };
  } else {
    return {
      ...defaultOptions,
      deviceId: { exact: camera }
    };
  }
};

/**
 * Get video constraints for preview (smaller size)
 * @param {string} selectedCamera - The selected camera value
 * @returns {Object} Video constraints object for preview
 */
export const getPreviewVideoConstraints = (selectedCamera = null) => {
  return getVideoConstraints(selectedCamera, {
    width: 320,
    height: 240
  });
};

/**
 * Save camera preference to localStorage
 * @param {string} cameraValue - The camera value to save
 */
export const saveCameraPreference = (cameraValue) => {
  localStorage.setItem('selectedCamera', cameraValue);
};

/**
 * Detect available cameras
 * @returns {Promise<Array>} Array of available camera options
 */
export const detectAvailableCameras = async () => {
  try {
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        (device) => device.kind === 'videoinput'
      );

      const cameraOptions = [];

      // Only add cameras that have actual device IDs and labels
      videoDevices.forEach((device, index) => {
        if (device.deviceId && device.deviceId !== 'default' && device.label) {
          cameraOptions.push({
            value: device.deviceId,
            label: device.label
          });
        } else if (device.deviceId && device.deviceId !== 'default') {
          // If no label but has deviceId, create a generic name
          cameraOptions.push({
            value: device.deviceId,
            label: `Camera ${index + 1}`
          });
        }
      });

      // If no specific cameras found, add facingMode options as fallback
      if (cameraOptions.length === 0) {
        cameraOptions.push(
          { value: 'user', label: 'Front Camera' },
          { value: 'environment', label: 'Back Camera' }
        );
      }

      return cameraOptions;
    }
  } catch (error) {
    console.error('Error detecting cameras:', error);
  }

  // Fallback when no cameras detected
  return [
    { value: 'user', label: 'Front Camera' },
    { value: 'environment', label: 'Back Camera' }
  ];
};

/**
 * Get camera settings for Webcam component
 * @param {Object} additionalProps - Additional props to merge
 * @returns {Object} Props for Webcam component
 */
export const getWebcamProps = (additionalProps = {}) => {
  const selectedCamera = getSelectedCamera();
  const videoConstraints = getVideoConstraints(selectedCamera);

  return {
    audio: false,
    screenshotFormat: 'image/png',
    videoConstraints,
    style: { border: '2px solid #2F5597' },
    ...additionalProps
  };
};

/**
 * Get camera settings for preview Webcam component
 * @param {Object} additionalProps - Additional props to merge
 * @returns {Object} Props for preview Webcam component
 */
export const getPreviewWebcamProps = (additionalProps = {}) => {
  const selectedCamera = getSelectedCamera();
  const videoConstraints = getPreviewVideoConstraints(selectedCamera);

  return {
    audio: false,
    screenshotFormat: 'image/png',
    videoConstraints,
    style: {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    ...additionalProps
  };
};
